import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Equal, Loader2, Medal, RotateCcw, Save } from "lucide-react";
import { getVzRankSettings, saveVzRankSettings } from "@/lib/verizonSalesApi";
import { QUOTA_CATEGORIES } from "@/lib/vzQuotas";

const LABELS = Object.fromEntries(QUOTA_CATEGORIES.map((c) => [c.key, c.label]));

export const OverallRankSettingsPanel = ({ onSaved }) => {
  const [data, setData] = useState(null);
  const [weights, setWeights] = useState({});
  const [cap, setCap] = useState(true);
  const [saving, setSaving] = useState(false);

  const apply = (overall, valid) => {
    setCap(overall.cap);
    setWeights(Object.fromEntries(overall.kpis.filter((k) => valid.includes(k.key)).map((k) => [k.key, k.weight])));
  };
  useEffect(() => { getVzRankSettings().then((d) => { setData(d); apply(d.overall, d.options.kpis); }).catch(() => toast.error("Failed to load settings")); }, []);

  const save = async (overall) => {
    setSaving(true);
    try { const r = await saveVzRankSettings({ overall }); apply(r.overall, data.options.kpis); toast.success("Overall Rank settings saved"); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const current = () => ({ cap, kpis: Object.entries(weights).map(([key, weight]) => ({ key, weight })) });
  const totalW = Object.values(weights).reduce((a, w) => a + (Number(w) || 0), 0);

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;
  const label = (k) => data.options.custom_labels[k] || LABELS[k] || k;
  return (
    <Card className="border-slate-200" data-testid="vz-overall-settings">
      <CardHeader className="pb-2 flex-row items-center justify-between flex-wrap gap-2">
        <div>
          <CardTitle className="text-base flex items-center gap-2"><Medal className="w-4 h-4 text-amber-500" /> Overall Rank</CardTitle>
          <p className="text-xs text-slate-500 mt-0.5">Pick the KPIs that make up each store's company-wide Overall Rank and how much each counts. Goals come from Store Goals (quota bar) — add new KPIs there first.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={() => setWeights(Object.fromEntries(Object.keys(weights).map((k) => [k, 1])))} data-testid="vz-overall-equal-btn"><Equal className="w-4 h-4 mr-1" /> Equal weights</Button>
          <Button size="sm" variant="outline" onClick={() => save(data.defaults.overall)} disabled={saving} data-testid="vz-overall-reset-btn"><RotateCcw className="w-4 h-4 mr-1" /> Reset to defaults</Button>
          <Button size="sm" onClick={() => save(current())} disabled={saving} className="bg-teal hover:bg-teal/90" data-testid="vz-overall-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Switch checked={cap} onCheckedChange={setCap} data-testid="vz-overall-cap" /> Cap each KPI at 100% of goal
        </label>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {data.options.kpis.map((k) => {
            const on = k in weights;
            return (
              <div key={k} className={`flex items-center gap-2 rounded-lg border px-3 py-2 ${on ? "border-teal/40 bg-teal/5" : "border-slate-200"}`} data-testid={`vz-overall-kpi-${k}`}>
                <Checkbox checked={on} data-testid={`vz-overall-kpi-${k}-check`} onCheckedChange={(v) => {
                  const n = { ...weights }; if (v) n[k] = 1; else delete n[k]; setWeights(n);
                }} />
                <span className="text-sm text-navy flex-1">{label(k)}</span>
                {on && (
                  <>
                    <Input type="number" min="0.1" step="0.5" value={weights[k]} onChange={(e) => setWeights({ ...weights, [k]: e.target.value })}
                      className="h-8 w-16 text-sm" data-testid={`vz-overall-kpi-${k}-weight`} />
                    <span className="text-xs text-slate-400 w-10 text-right">{totalW ? `${Math.round(((Number(weights[k]) || 0) / totalW) * 100)}%` : ""}</span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
